import React, { useState } from 'react';
import Modal from 'react-modal';
import './reviewModal.css';

const ReviewModal = ({ isOpen, onClose, hiringId, reviewType, onReviewSubmitted }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (rating < 1 || rating > 5) {
      setError('Por favor selecciona una calificación de 1 a 5 estrellas');
      return;
    }

    if (!comment || comment.trim().length === 0) {
      setError('Por favor escribe un comentario');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // User info is extracted from token by middleware, no need to send in body
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/reviews`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          hiringId: parseInt(hiringId),
          reviewType,
          rating: parseInt(rating),
          comment: comment.trim() || null
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Error al enviar la reseña');
      }

      const data = await response.json();
      
      // Show success message
      setSuccess(true);
      
      if (onReviewSubmitted) {
        onReviewSubmitted(data);
      }

      // Reset form and close modal after showing success message (1 second)
      setTimeout(() => {
        setRating(0);
        setComment('');
        setError(null);
        setSuccess(false);
        onClose();
      }, 1000);
    } catch (err) {
      setError(err.message || 'Error al enviar la reseña. Por favor intenta de nuevo.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!submitting) {
      setRating(0);
      setComment('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const renderStars = () => {
    return (
      <div className="review-modal__stars">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`review-modal__star ${star <= rating ? 'active' : ''}`}
            onClick={() => setRating(star)}
            disabled={submitting}
            aria-label={`Calificar ${star} estrellas`}
          >
            ★
          </button>
        ))}
      </div>
    );
  };

  const getTitle = () => {
    if (reviewType === 'employee_review') {
      return 'Calificar Empleado';
    } else if (reviewType === 'restaurant_review') {
      return 'Calificar Restaurante';
    }
    return 'Dejar Reseña';
  };

  const getDescription = () => {
    if (reviewType === 'employee_review') {
      return '¿Cómo fue tu experiencia trabajando con este empleado?';
    } else if (reviewType === 'restaurant_review') {
      return '¿Cómo fue tu experiencia trabajando en este restaurante?';
    }
    return 'Comparte tu experiencia';
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Review Modal"
      className="review-modal"
      overlayClassName="review-modal__overlay"
      ariaHideApp={false}
    >
      <div className="review-modal__container">
        <button
          className="review-modal__close-button"
          onClick={handleClose}
          disabled={submitting}
          aria-label="Cerrar"
        >
          ×
        </button>

        <h2 className="review-modal__title">{getTitle()}</h2>
        <p className="review-modal__description">{getDescription()}</p>

        <form onSubmit={handleSubmit} className="review-modal__form">
          <div className="review-modal__rating-section">
            <label className="review-modal__label">Calificación *</label>
            {renderStars()}
            {rating > 0 && (
              <p className="review-modal__rating-text">
                {rating === 1 && 'Muy malo'}
                {rating === 2 && 'Malo'}
                {rating === 3 && 'Regular'}
                {rating === 4 && 'Bueno'}
                {rating === 5 && 'Excelente'}
              </p>
            )}
          </div>

          <div className="review-modal__comment-section">
            <label htmlFor="comment" className="review-modal__label">
              Comentario *
            </label>
            <textarea
              id="comment"
              className="review-modal__textarea"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comparte más detalles sobre tu experiencia..."
              rows={5}
              maxLength={500}
              disabled={submitting}
            />
            <p className="review-modal__char-count">{comment.length}/500</p>
          </div>

          {error && (
            <div className="review-modal__error">
              {error}
            </div>
          )}

          {success && (
            <div className="review-modal__success">
              ✅ ¡Reseña enviada exitosamente!
            </div>
          )}

          <div className="review-modal__actions">
            <button
              type="button"
              className="review-modal__cancel-button"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="review-modal__submit-button"
              disabled={submitting || rating < 1 || !comment || comment.trim().length === 0}
            >
              {submitting ? 'Enviando...' : 'Aceptar'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ReviewModal;

